import * as THREE from 'three'

export class Ship {
  mesh: THREE.Group
  bowSplashLeft: THREE.Mesh
  bowSplashRight: THREE.Mesh
  wakeParticles!: THREE.Points
  particlePositions!: Float32Array
  particleVelocities: THREE.Vector3[] = []
  particleLifetimes!: Float32Array

  constructor(scene: THREE.Scene) {
    this.mesh = new THREE.Group()
    this.mesh.scale.set(1.5, 1.5, 1.5)

    const hullShape = new THREE.Shape()
    hullShape.moveTo(0, 3)
    hullShape.lineTo(1.5, -2)
    hullShape.lineTo(-1.5, -2)
    hullShape.lineTo(0, 3)

    const hullGeo = new THREE.ExtrudeGeometry(hullShape, {
      steps: 1,
      depth: 0.4,
      bevelEnabled: true,
      bevelThickness: 0.1,
      bevelSize: 0.1,
      bevelSegments: 2
    })

    const hullMesh = new THREE.Mesh(
      hullGeo,
      new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7 })
    )
    hullMesh.rotation.x = -Math.PI / 2
    hullMesh.position.y = 0.2
    this.mesh.add(hullMesh)

    const mast = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 5),
      new THREE.MeshStandardMaterial({ color: 0x654321 })
    )
    mast.position.set(0, 2.5, 0.5)
    this.mesh.add(mast)

    const sailShape = new THREE.Shape()
    sailShape.moveTo(0, 4.5)
    sailShape.lineTo(1.2, 2)
    sailShape.lineTo(0, 2)
    sailShape.lineTo(0, 4.5)

    const sail = new THREE.Mesh(
      new THREE.ExtrudeGeometry(sailShape, { depth: 0.05, bevelEnabled: false }),
      new THREE.MeshStandardMaterial({ color: 0xf5f5dc, side: THREE.DoubleSide })
    )
    sail.position.set(0, 0, 0.5)
    this.mesh.add(sail)

    const splashGeo = new THREE.PlaneGeometry(0.9, 1.4)
    const splashMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })

    this.bowSplashLeft = new THREE.Mesh(splashGeo, splashMat.clone())
    this.bowSplashLeft.position.set(-0.45, 0.25, 3.0)
    this.bowSplashLeft.rotation.x = -Math.PI / 2
    this.bowSplashLeft.rotation.z = Math.PI * 0.18
    this.mesh.add(this.bowSplashLeft)

    this.bowSplashRight = new THREE.Mesh(splashGeo, splashMat.clone())
    this.bowSplashRight.position.set(0.45, 0.25, 3.0)
    this.bowSplashRight.rotation.x = -Math.PI / 2
    this.bowSplashRight.rotation.z = -Math.PI * 0.18
    this.mesh.add(this.bowSplashRight)

    this.createWakeSystem()

    this.mesh.position.set(0, 0, 0)
    scene.add(this.mesh)
  }

  createWakeSystem() {
    const particleCount = 180
    this.particlePositions = new Float32Array(particleCount * 3)
    this.particleVelocities = []
    this.particleLifetimes = new Float32Array(particleCount)

    const geometry = new THREE.BufferGeometry()

    for (let i = 0; i < particleCount; i++) {
      this.particlePositions[i * 3] = 0
      this.particlePositions[i * 3 + 1] = -100
      this.particlePositions[i * 3 + 2] = 0
      this.particleVelocities.push(new THREE.Vector3())
      this.particleLifetimes[i] = 0
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3))

    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.22,
      transparent: true,
      opacity: 0.75,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })

    this.wakeParticles = new THREE.Points(geometry, material)
    this.mesh.add(this.wakeParticles)
  }

  update(time: number, speed: number = 0) {
    this.mesh.position.y = Math.sin(time * 0.5) * 0.18
    this.mesh.rotation.z = Math.sin(time * 0.3) * 0.02

    const splashOpacity = Math.min(0.18 + speed / 8, 0.9)
    ;(this.bowSplashLeft.material as THREE.MeshBasicMaterial).opacity = splashOpacity
    ;(this.bowSplashRight.material as THREE.MeshBasicMaterial).opacity = splashOpacity

    const pulse = 1 + Math.sin(time * 18) * 0.25
    this.bowSplashLeft.scale.set(pulse, pulse, 1)
    this.bowSplashRight.scale.set(pulse, pulse, 1)

    this.updateWake(speed)
  }

  updateWake(speed: number) {
    const positions = this.wakeParticles.geometry.attributes.position.array as Float32Array

    for (let i = 0; i < this.particleLifetimes.length; i++) {
      const idx = i * 3

      if (this.particleLifetimes[i] > 0) {
        this.particleLifetimes[i] -= 0.018

        positions[idx] += this.particleVelocities[i].x
        positions[idx + 1] += this.particleVelocities[i].y
        positions[idx + 2] += this.particleVelocities[i].z

        this.particleVelocities[i].y -= 0.004
        this.particleVelocities[i].x *= 0.99
        this.particleVelocities[i].z *= 0.995

        if (this.particleLifetimes[i] <= 0) {
          positions[idx + 1] = -100
        }
      } else if (speed > 0.3 && Math.random() < 0.08) {
        // bow impact split: emit from left/right bow and throw outward/backward
        const side = Math.random() < 0.5 ? -1 : 1
        positions[idx] = side * (0.35 + Math.random() * 0.25)
        positions[idx + 1] = 0.28
        positions[idx + 2] = 2.7 + Math.random() * 0.4

        this.particleVelocities[i].x = side * (0.02 + Math.random() * 0.03)
        this.particleVelocities[i].y = 0.03 + Math.random() * 0.04
        this.particleVelocities[i].z = -(0.05 + speed * 0.008 + Math.random() * 0.03)

        this.particleLifetimes[i] = 0.7 + Math.random() * 0.5
      }
    }

    this.wakeParticles.geometry.attributes.position.needsUpdate = true
  }
}
